import type { ToastProps } from '#types/notification'

export interface Toast {
  id?: string
  props?: ToastProps
  autoClose?: boolean
  delay?: number
  promise?: boolean
  restoreFocus?: () => void
}

const toasts = ref<Toast[]>([])

export default createGlobalState(() => {
  const { toasterDismissDelay } = useAppConfig()
  const INTERVAL = 200
  const timers: Record<string, number> = {}
  const promises: Record<string, (value: void) => void> = {}

  const { pause, resume } = useIntervalFn(() => {
    Object.keys(timers).forEach((key) => {
      timers[key] -= INTERVAL
      if (timers[key] <= 0)
        remove(key)
    })
  }, INTERVAL)

  function add({ props, autoClose = true, delay, promise, id: passedId, restoreFocus }: Toast = {}) {
    const id = passedId ?? `toast_${getIdClient()}`
    if (autoClose) {
      timers[id] = delay || toasterDismissDelay
    }

    toasts.value.push({ props, id, restoreFocus })

    if (promise) {
      const resolvers = promiseWithResolvers()
      promises[id] = resolvers.resolve
      return resolvers.promise
    }
  }

  function remove(id) {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
    delete timers[id]
    promises[id]?.(void 0)
  }

  watch(
    () => toasts.value.length > 0,
    (hasToasts) => {
      if (hasToasts && toasterDismissDelay) resume()
      else pause()
    },
    { immediate: true }
  )

  return {
    toasts: readonly(toasts),
    add,
    remove,
    pause,
    resume
  }
})
